import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '../../../../../atoms/Icon'
import { ExpandArrow } from '../../../../../atoms/ExpandArrow'
import { AnalysisSectionHeader } from '../../../../../AnalysisSectionHeader'
import {
  AnalysisMetricsGrid,
  AnalysisMetricCard,
  MetricHeader,
  MetricIcon,
  MetricTitle,
  MetricValue,
} from '../../../../../atoms/AnalysisMetricCard'
import { Box } from '../../../../../atoms/Box'
import {
  translateRdppfZoneName,
  restrictionLabel,
} from '../../../../../../utils/rdppfTranslations'
import { useAnalysisMode } from '../../../contexts/AnalysisModeContext'
import { useAnalysisMapSectionData } from '../../../contexts/MapSectionDataContext'
import {
  RestrictionsContainer,
  RestrictionContentContainer,
  RestrictionCategory,
  CategoryHeader,
  CategoryIcon,
  CategoryTitle,
  CategoryCount,
  RestrictionItems,
  RestrictionItem,
  RestrictionHeader,
  RestrictionMain,
  RestrictionName,
  RestrictionId,
  RestrictionStatus,
  RestrictionToggle,
  RestrictionDetails,
  DetailGrid,
  DetailRow,
  DetailLabel,
  DetailValue,
  DetailLink,
  ProtectedObject,
  ObjectTitle,
  ObjectSeparator,
  NoRestrictionMessage,
  LegalNotice,
  DataSources,
  SourceLabel,
  SourceList,
  Badge,
} from './RestrictionsTab.styles'

interface RestrictionData {
  id: string
  name: string
  type: string
  isAffecting: boolean
  registryNumber?: string
  classification?: string
  content?: string
  status?: string
  additionalInfo?: string
  externalLink?: string
  area?: number
  integrationDate?: string
  heritageLink?: string
}

interface GroupedRestriction {
  id: string
  name: string
  restrictions: RestrictionData[]
  isAffecting: boolean
  totalCount: number
  affectingCount: number
  type?: string
}

/**
 * Shared RestrictionsTab for read-only display of RDPPF restrictions.
 * Used by public pages (land analysis).
 * Reads entity data from AnalysisModeContext.
 * Uses same UI components and translation keys as the main app.
 */
export const RestrictionsTab: React.FC = () => {
  const { t, i18n } = useTranslation('restrictions')
  const { entity } = useAnalysisMode()
  const { setActiveSection, updateSectionData } = useAnalysisMapSectionData()
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  // Push restrictions data to map when available
  useEffect(() => {
    setActiveSection('restrictions')
    const rdppfs = entity?.rdppfs
    if (rdppfs && Array.isArray(rdppfs)) {
      const restrictionsData = rdppfs.map((rdppf: any, index: number) => ({
        id: rdppf.rdppf_id || `rdppf-${index}`,
        name: restrictionLabel(rdppf.rdppf_type ?? rdppf, i18n.language),
        type: rdppf.name || 'Unknown',
        rdppfTypeId: rdppf.rdppf_type_id || rdppf.rdppf_id,
        geometry: rdppf.geometry,
        coveragePerc: rdppf.cover_area_perc ?? null,
        isAffecting: true,
      }))
      updateSectionData({ restrictions: restrictionsData })
    }
  }, [entity?.rdppfs, setActiveSection, updateSectionData])

  const toggleRestriction = (id: string) => {
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedItems(newExpanded)
  }

  const processRestrictions = (): {
    affecting: GroupedRestriction[]
    nonAffecting: GroupedRestriction[]
  } => {
    const allRestrictions: RestrictionData[] = []
    const regionRestrictions = entity?.regionRestrictions || []

    if (regionRestrictions.length > 0) {
      // Process region restrictions (all types for the region)
      // Compare against land.rdppfs to determine affecting vs non-affecting
      regionRestrictions.forEach((rdppfType: any) => {
        const landRdppfs =
          entity?.rdppfs?.filter(
            (lr: any) =>
              lr.rdppf_type_id?.toString() === rdppfType.id?.toString()
          ) || []

        if (landRdppfs.length > 0) {
          // This restriction type affects the land
          landRdppfs.forEach((landRdppf: any, landIndex: number) => {
            const isAffectingLand = true

            allRestrictions.push({
              id: `land-rdppf-type-${rdppfType.id}-${landIndex}`,
              name: restrictionLabel(rdppfType, i18n.language),
              type: rdppfType.name || 'Unknown',
              isAffecting: isAffectingLand,
              status: isAffectingLand ? 'Active' : 'Clear',
              content:
                typeof landRdppf.cover_area_perc === 'number'
                  ? t('messages.coverage', {
                      percent: ((landRdppf.cover_area_perc ?? 0) * 100).toFixed(
                        1
                      ),
                    })
                  : '',
              area:
                typeof landRdppf.cover_area_perc === 'number'
                  ? (landRdppf.cover_area_perc ?? 0) * 100
                  : undefined,
              registryNumber: landRdppf.rdppf_id,
            })
          })
        } else {
          // This restriction type does NOT affect the land
          allRestrictions.push({
            id: `region-rdppf-type-${rdppfType.id}`,
            name: restrictionLabel(rdppfType, i18n.language),
            type: rdppfType.name || 'Unknown',
            isAffecting: false,
            status: 'Clear',
            content: t('messages.notApplicable'),
          })
        }
      })
    } else {
      // Fallback: no region restrictions available, use land.rdppfs only
      if (entity?.rdppfs && entity.rdppfs.length > 0) {
        entity.rdppfs.forEach((rdppf: any, index: number) => {
          const isAffectingLand = rdppf.active !== false

          allRestrictions.push({
            id: `rdppf-${index}`,
            name: restrictionLabel(rdppf.rdppf_type ?? rdppf, i18n.language),
            type: rdppf.rdppf_type?.name || 'Unknown',
            isAffecting: isAffectingLand,
            status: isAffectingLand ? 'Active' : 'Clear',
            content:
              typeof rdppf.cover_area_perc === 'number'
                ? t('messages.coverage', {
                    percent: ((rdppf.cover_area_perc ?? 0) * 100).toFixed(1),
                  })
                : '',
            area:
              typeof rdppf.cover_area_perc === 'number'
                ? (rdppf.cover_area_perc ?? 0) * 100
                : undefined,
            registryNumber: rdppf.rdppf_id,
          })
        })
      }
    }

    // Group restrictions by name
    const groupedByName = allRestrictions.reduce(
      (acc, restriction) => {
        const nameKey = restriction.name
        if (!acc[nameKey]) {
          acc[nameKey] = []
        }
        acc[nameKey].push(restriction)
        return acc
      },
      {} as Record<string, RestrictionData[]>
    )

    const affecting: GroupedRestriction[] = []
    const nonAffecting: GroupedRestriction[] = []

    Object.entries(groupedByName).forEach(([name, restrictions]) => {
      const affectingRestrictions = restrictions.filter((r) => r.isAffecting)
      const nonAffectingRestrictions = restrictions.filter(
        (r) => !r.isAffecting
      )

      if (affectingRestrictions.length > 0) {
        affecting.push({
          id: `affecting-${name}`,
          name,
          restrictions: affectingRestrictions,
          isAffecting: true,
          totalCount: affectingRestrictions.length,
          affectingCount: affectingRestrictions.length,
        })
      }

      if (nonAffectingRestrictions.length > 0) {
        nonAffecting.push({
          id: `non-affecting-${name}`,
          name,
          restrictions: nonAffectingRestrictions,
          isAffecting: false,
          totalCount: nonAffectingRestrictions.length,
          affectingCount: 0,
        })
      }
    })

    // Check for protected buildings
    const protectedBuildings: RestrictionData[] = []
    if (entity?.buildings && entity.buildings.length > 0) {
      entity.buildings.forEach((building: any, index: number) => {
        if (building.buildings_administrative) {
          building.buildings_administrative.forEach(
            (admin: any, adminIndex: number) => {
              if (admin.protected) {
                protectedBuildings.push({
                  id: `protected-${index}-${adminIndex}`,
                  name: t('heritage.protectedObjects'),
                  type: t('heritage.heritageProtection'),
                  isAffecting: true,
                  status: t('status.protected'),
                  classification: 'MS-c3',
                  integrationDate: admin.building_year
                    ? `${admin.building_year}`
                    : undefined,
                  area: admin.area,
                  content: t('heritage.buildingUnderProtection'),
                })
              }
            }
          )
        }
      })
    }

    if (protectedBuildings.length > 0) {
      affecting.push({
        id: 'affecting-Heritage Protection',
        name: t('heritage.protectedObjects'),
        type: t('heritage.heritageProtection'),
        restrictions: protectedBuildings,
        isAffecting: true,
        totalCount: protectedBuildings.length,
        affectingCount: protectedBuildings.length,
      })
    }

    return { affecting, nonAffecting }
  }

  if (!entity) {
    return (
      <RestrictionsContainer>
        <div style={{ textAlign: 'center', padding: '48px' }}>
          {t('loading')}
        </div>
      </RestrictionsContainer>
    )
  }

  if (
    (entity as any).restrictions_status === 'not_loaded' ||
    (entity as any).restrictions_status === 'unavailable'
  ) {
    return <RestrictionsContainer>{t('noData')}</RestrictionsContainer>
  }

  const { affecting, nonAffecting } = processRestrictions()
  const totalAnalyzedTypes = affecting.length + nonAffecting.length
  const totalAffectingRestrictions = affecting.reduce(
    (sum, group) => sum + group.totalCount,
    0
  )
  const totalNonAffectingRestrictions = nonAffecting.reduce(
    (sum, group) => sum + group.totalCount,
    0
  )

  // Format address for subtitle
  const formatAddress = () => {
    if (entity.address_full) return entity.address_full
    if (entity.address_street && entity.address_postal_code) {
      return `${entity.address_street} ${entity.address_street_nb || ''}, ${entity.address_postal_code}`.trim()
    }
    return entity.municipality_name || ''
  }

  return (
    <RestrictionsContainer>
      <AnalysisSectionHeader
        title={t('title')}
        subtitle={t('subtitle', { address: formatAddress() })}
      />

      <RestrictionContentContainer>
        <AnalysisMetricsGrid>
          <AnalysisMetricCard>
            <MetricHeader>
              <MetricIcon color="#3b82f6">
                <Icon name="layers" size="small" />
              </MetricIcon>
              <MetricTitle>{t('metrics.typesAnalyzed')}</MetricTitle>
            </MetricHeader>
            <Box mt="8px">
              <MetricValue>{totalAnalyzedTypes}</MetricValue>
            </Box>
          </AnalysisMetricCard>

          <AnalysisMetricCard>
            <MetricHeader>
              <MetricIcon color="#f59e0b">
                <Icon name="warning" size="small" />
              </MetricIcon>
              <MetricTitle>{t('metrics.affectingPlot')}</MetricTitle>
            </MetricHeader>
            <Box mt="8px">
              <MetricValue style={{ color: '#f59e0b' }}>
                {totalAffectingRestrictions}
              </MetricValue>
            </Box>
          </AnalysisMetricCard>

          <AnalysisMetricCard>
            <MetricHeader>
              <MetricIcon color="#10b981">
                <Icon name="check" size="small" />
              </MetricIcon>
              <MetricTitle>{t('metrics.notAffecting')}</MetricTitle>
            </MetricHeader>
            <Box mt="8px">
              <MetricValue style={{ color: '#10b981' }}>
                {totalNonAffectingRestrictions}
              </MetricValue>
            </Box>
          </AnalysisMetricCard>
        </AnalysisMetricsGrid>

        {/* Restrictions Categories */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            marginTop: '24px',
          }}
        >
          {/* Affecting Restrictions */}
          {affecting.length > 0 && (
            <RestrictionCategory>
              <CategoryHeader>
                <CategoryIcon $variant="danger">
                  <Icon name="warning" size="medium" />
                </CategoryIcon>
                <CategoryTitle>{t('categories.affecting')}</CategoryTitle>
                <CategoryCount>
                  {affecting.length}/{totalAnalyzedTypes}
                </CategoryCount>
              </CategoryHeader>

              <RestrictionItems>
                {affecting.map((group) => (
                  <RestrictionItem
                    key={group.id}
                    $expanded={expandedItems.has(group.id)}
                  >
                    <RestrictionHeader
                      onClick={() => toggleRestriction(group.id)}
                    >
                      <RestrictionMain>
                        <RestrictionName>
                          {translateRdppfZoneName(group.name, t)}
                        </RestrictionName>
                        <RestrictionId>
                          {group.totalCount > 1
                            ? t('instances.multiple', {
                                count: group.totalCount,
                              })
                            : t('instances.single')}
                        </RestrictionId>
                      </RestrictionMain>
                      <RestrictionToggle>
                        <ExpandArrow
                          $expanded={expandedItems.has(group.id)}
                          direction="down"
                        />
                      </RestrictionToggle>
                    </RestrictionHeader>

                    {expandedItems.has(group.id) && (
                      <RestrictionDetails>
                        {group.name === t('heritage.protectedObjects') &&
                        entity.buildings ? (
                          <>
                            {entity.buildings.map(
                              (building: any, buildingIndex: number) => {
                                const protectedAdmin =
                                  building.buildings_administrative?.find(
                                    (admin: any) => admin.protected
                                  )
                                if (!protectedAdmin) return null

                                return (
                                  <div key={buildingIndex}>
                                    {buildingIndex > 0 && <ObjectSeparator />}
                                    <ProtectedObject>
                                      <ObjectTitle>
                                        {building.addresses?.[0]?.street
                                          ? `${building.addresses[0].street} (${t('heritage.protectedBuilding')} ${buildingIndex + 1})`
                                          : `${t('heritage.protectedBuilding')} ${buildingIndex + 1}`}
                                      </ObjectTitle>
                                      <DetailGrid>
                                        <DetailRow>
                                          <DetailLabel>
                                            {t('labels.type')}
                                          </DetailLabel>
                                          <DetailValue>
                                            {t('heritage.protectedBuilding')}
                                          </DetailValue>
                                        </DetailRow>
                                        <DetailRow>
                                          <DetailLabel>
                                            {t('labels.classification')}
                                          </DetailLabel>
                                          <DetailValue>MS-c3</DetailValue>
                                        </DetailRow>
                                        {protectedAdmin.building_year && (
                                          <DetailRow>
                                            <DetailLabel>
                                              {t('labels.integrationDate')}
                                            </DetailLabel>
                                            <DetailValue>
                                              {protectedAdmin.building_year}
                                            </DetailValue>
                                          </DetailRow>
                                        )}
                                        {protectedAdmin.area && (
                                          <DetailRow>
                                            <DetailLabel>
                                              {t('labels.area')}
                                            </DetailLabel>
                                            <DetailValue>
                                              {protectedAdmin.area.toLocaleString()}{' '}
                                              m²
                                            </DetailValue>
                                          </DetailRow>
                                        )}
                                        <DetailRow $fullWidth>
                                          <DetailLabel>
                                            {t('labels.heritageDatabase')}
                                          </DetailLabel>
                                          <DetailLink href="#" target="_blank">
                                            {t('labels.viewInSipatrimoine')}
                                            <Icon
                                              name="arrow-right"
                                              size="small"
                                            />
                                          </DetailLink>
                                        </DetailRow>
                                      </DetailGrid>
                                    </ProtectedObject>
                                  </div>
                                )
                              }
                            )}
                          </>
                        ) : (
                          <>
                            {group.restrictions.map(
                              (restriction, restrictionIndex) => (
                                <div key={restriction.id}>
                                  {restrictionIndex > 0 && <ObjectSeparator />}

                                  {group.totalCount > 1 && (
                                    <ObjectTitle>
                                      {t('instances.instanceNumber', {
                                        number: restrictionIndex + 1,
                                      })}
                                      {restriction.registryNumber &&
                                        ` (${restriction.registryNumber})`}
                                    </ObjectTitle>
                                  )}

                                  <DetailGrid>
                                    {restriction.registryNumber && (
                                      <DetailRow>
                                        <DetailLabel>
                                          {t('labels.registryNumber')}
                                        </DetailLabel>
                                        <DetailValue>
                                          {restriction.registryNumber}
                                        </DetailValue>
                                      </DetailRow>
                                    )}

                                    <DetailRow>
                                      <DetailLabel>
                                        {t('labels.class')}
                                      </DetailLabel>
                                      <DetailValue>
                                        {restriction.type}
                                      </DetailValue>
                                    </DetailRow>

                                    <DetailRow>
                                      <DetailLabel>
                                        {t('labels.content')}
                                      </DetailLabel>
                                      <DetailValue>
                                        {restriction.content}
                                      </DetailValue>
                                    </DetailRow>

                                    <DetailRow>
                                      <DetailLabel>
                                        {t('labels.status')}
                                      </DetailLabel>
                                      <DetailValue>
                                        <Badge
                                          $variant={
                                            restriction.isAffecting
                                              ? restriction.status === 'Active'
                                                ? 'warning'
                                                : 'success'
                                              : 'success'
                                          }
                                        >
                                          {restriction.status ||
                                            t('status.valid')}
                                        </Badge>
                                      </DetailValue>
                                    </DetailRow>

                                    {restriction.area !== undefined &&
                                      restriction.area > 0 && (
                                        <DetailRow>
                                          <DetailLabel>
                                            {t('labels.affectedArea')}
                                          </DetailLabel>
                                          <DetailValue>
                                            {restriction.area.toFixed(1)}%
                                          </DetailValue>
                                        </DetailRow>
                                      )}

                                    {restriction.externalLink && (
                                      <DetailRow $fullWidth>
                                        <DetailLabel>
                                          {t('labels.additionalInformation')}
                                        </DetailLabel>
                                        <DetailLink
                                          href={restriction.externalLink}
                                          target="_blank"
                                        >
                                          {t('labels.viewDocumentation')}
                                          <Icon
                                            name="arrow-right"
                                            size="small"
                                          />
                                        </DetailLink>
                                      </DetailRow>
                                    )}
                                  </DetailGrid>
                                </div>
                              )
                            )}
                          </>
                        )}
                      </RestrictionDetails>
                    )}
                  </RestrictionItem>
                ))}
              </RestrictionItems>
            </RestrictionCategory>
          )}

          {/* Non-Affecting Restrictions */}
          <RestrictionCategory>
            <CategoryHeader>
              <CategoryIcon $variant="success">
                <Icon name="check" size="medium" />
              </CategoryIcon>
              <CategoryTitle>{t('categories.notAffecting')}</CategoryTitle>
              <CategoryCount>
                {nonAffecting.length}/{totalAnalyzedTypes}
              </CategoryCount>
            </CategoryHeader>

            <RestrictionItems>
              {nonAffecting.map((group) => (
                <RestrictionItem
                  key={group.id}
                  $expanded={expandedItems.has(group.id)}
                >
                  <RestrictionHeader
                    onClick={() => toggleRestriction(group.id)}
                  >
                    <RestrictionMain>
                      <RestrictionName>
                        {translateRdppfZoneName(group.name, t)}
                      </RestrictionName>
                      <RestrictionStatus>{t('status.clear')}</RestrictionStatus>
                    </RestrictionMain>
                    <RestrictionToggle>
                      <ExpandArrow
                        $expanded={expandedItems.has(group.id)}
                        direction="down"
                      />
                    </RestrictionToggle>
                  </RestrictionHeader>

                  {expandedItems.has(group.id) && (
                    <RestrictionDetails>
                      <NoRestrictionMessage>
                        {t('messages.notApplicable')}
                      </NoRestrictionMessage>
                    </RestrictionDetails>
                  )}
                </RestrictionItem>
              ))}
            </RestrictionItems>
          </RestrictionCategory>
        </div>

        {/* Legal Notice */}
        <LegalNotice>
          <Icon name="info" size="medium" />
          <p>{t('legalNotice.disclaimer')}</p>
        </LegalNotice>

        {/* Data Sources */}
        <DataSources>
          <SourceLabel>{t('legalNotice.sources')}</SourceLabel>
          <SourceList>
            {t('legalNotice.sourcesList')}
            {entity.rdppf_land_link && (
              <>
                {' \u2022 '}
                <DetailLink href={entity.rdppf_land_link} target="_blank">
                  {t('legalNotice.rdppfOfficialReport')}
                </DetailLink>
              </>
            )}
          </SourceList>
        </DataSources>
      </RestrictionContentContainer>
    </RestrictionsContainer>
  )
}
